/**
 * React hooky pro Tracer-AN nabíječ — data fetching + selektory ze Zustand storu.
 */

import { useCallback, useEffect } from "react";
import { toast } from "sonner";
import { getTracerList, getTracerSnapshot } from "@client/api/tracer";
import { useTracerStore } from "@client/store/tracer";

export const useFetchTracerList = () => {
  const setDevices = useTracerStore((s) => s.setDevices);
  return useCallback(async () => {
    try {
      const res = await getTracerList();
      setDevices(res.responseObject ?? []);
    } catch (err) {
      toast.error("Nepodařilo se načíst seznam nabíječů");
      console.error(err);
    }
  }, [setDevices]);
};

export const useFetchTracerSnapshot = (slug: string | undefined) => {
  const applyState = useTracerStore((s) => s.applyState);
  return useCallback(async () => {
    if (!slug) return;
    try {
      const res = await getTracerSnapshot(slug);
      const runtime = res.responseObject?.runtime;
      if (runtime) {
        applyState({
          slug,
          status: runtime.status,
          deviceInfo: runtime.deviceInfo ?? undefined,
          settings: runtime.settings ?? undefined,
          statistics: runtime.statistics ?? undefined,
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, [slug, applyState]);
};

export const useTracerList = () => {
  const devices = useTracerStore((s) => s.devices);
  const loaded = useTracerStore((s) => s.loaded);
  const fetch = useFetchTracerList();
  useEffect(() => {
    if (!loaded) void fetch();
  }, [loaded, fetch]);
  return { devices, loaded, refresh: fetch };
};

export const useTracerDevice = (slug: string | undefined) => {
  const device = useTracerStore((s) => s.devices.find((d) => d.slug === slug));
  const telemetry = useTracerStore((s) => (slug ? s.telemetry.get(slug) : undefined));
  const history = useTracerStore((s) => (slug ? s.history.get(slug) : undefined));
  return { device, telemetry, history };
};
